/** @jsx CanvaSX */
import {
  CanvaSX,
  type MountedObjects,
  renderElement,
  withContext,
} from '@lib/canvasx/canvasx';
import { FlexRow } from '@lib/canvasx/components/FlexRow';
import { Rect } from '@lib/canvasx/components/Rect';
import { normalizeSpacing } from '@lib/canvasx/shapes';
import { fabric } from 'fabric';
import {
  type ButtonState,
  ButtonStyles,
  formattedRgba,
} from '../canvasx/buttonStyles';
import { type CardData } from '../canvasx/fieldContents';
import { SquareIcon } from '../canvasx/SquareIcon';
import { type KeyPath } from '../collaboration/DataStoreRecordBehaviors';
import type WhiteboardController from '../controllers/WhiteboardController';
import { sanitizeShapeName, type ShapeName, shapeNameToClass } from '../shapes';
import type ShapeBase from '../shapes/ShapeBase';
import { type colorFilterMode } from '../shapes/ShapeImage';
import { type WhiteboardRecordTypeNames } from '../shared-types';
import tools from '../tools';
import type { MaskType } from '../tools/ToolImageMask';
import ToolOpenRecord from '../tools/ToolOpenRecord';
import { type StrokeType } from '../tools/ToolStrokeStyle';
import {
  type Connectable,
  type IntegrationRecord,
  type RecordId,
} from '../types';
import { pointInRect } from '../utils/geometry';
import { camelToSentenceCase } from '../utils/string';
import AnalyticsBehaviors from './AnalyticsBehaviors';
import CanvaSXBehaviors from './CanvaSXBehaviors';
import CollaborationBehaviors from './CollaborationBehaviors';
import CommentableBehaviors from './CommentableBehaviors';
import ConnectorAttachmentBehaviors from './ConnectorAttachmentBehaviors';
import LinkToUrlBehaviors, {
  type LinkToUrlBehaviorsInterface,
} from './LinkToUrlBehaviors';
import EmbeddedTextBehaviorsV2, {
  type EmbeddedTextBehaviors as IEmbeddedTextBehaviors,
  type TextProps,
} from './EmbeddedTextBehaviorsV2';
import EndlessStackBehaviors from './EndlessStackBehaviors';
import ExternalContentBehaviors from './ExternalContentBehaviors';
import FrameBehaviors from './FrameBehaviors';
import FrameMemberBehaviors from './FrameMemberBehaviors';
import {
  hasEmbeddedTextBehaviors,
  hasIntegrationRecordReference,
  hasRecordReference,
} from './guards';
import HighlightBehaviors from './HighlightBehaviors';
import { OPEN_RECORD } from './IconsHelper';
import LabelBehaviors, { labelShapes } from './LabelBehaviors';
import LockBehaviors from './LockBehaviors';
import ReactionsBehaviors from './ReactionsBehaviors';
import ReadOnlyBehaviors from './ReadOnlyBehaviors';
import ShapePersistentControlBehaviors from './ShapePersistentControlBehaviors';
import SmartGuideBehaviors from './SmartGuideBehaviors';
import SmartZoneBehaviors from './SmartZoneBehaviors';
import StyleCopyPasteBehaviors from './StyleCopyPasteBehaviors';
import VotingBehaviors from './VotingBehaviors';
import InitAfterBehaviorConstructors from './InitAfterBehaviorConstructors';

export function shapeNameToDescription(name: string) {
  return camelToSentenceCase(name.replace(/(r)ect\b/gi, '$1ectangle'));
}

export type ShapeOptions = Partial<Shape> & {
  shapeName: ShapeName;
  disableSmartGuides?: boolean;
  controller: WhiteboardController;
};

// A geometric shape containing text
interface Shape
  extends fabric.Object,
    LinkToUrlBehaviorsInterface,
    VotingBehaviors,
    ShapePersistentControlBehaviors,
    ReactionsBehaviors,
    LockBehaviors,
    LabelBehaviors,
    CanvaSXBehaviors,
    ReadOnlyBehaviors,
    SmartGuideBehaviors,
    ConnectorAttachmentBehaviors,
    CollaborationBehaviors,
    IEmbeddedTextBehaviors,
    FrameBehaviors,
    FrameMemberBehaviors,
    CommentableBehaviors,
    SmartZoneBehaviors,
    EndlessStackBehaviors {}

@InitAfterBehaviorConstructors
@EndlessStackBehaviors
@LinkToUrlBehaviors
@VotingBehaviors
@SmartZoneBehaviors
@ShapePersistentControlBehaviors
@ReactionsBehaviors
@LockBehaviors
@AnalyticsBehaviors
@LabelBehaviors
@CanvaSXBehaviors
@ReadOnlyBehaviors
@SmartGuideBehaviors
@ConnectorAttachmentBehaviors
@CollaborationBehaviors
@EmbeddedTextBehaviorsV2
@HighlightBehaviors
@FrameBehaviors
@FrameMemberBehaviors
@CommentableBehaviors
@ExternalContentBehaviors
class Shape<S extends ShapeBase = ShapeBase>
  extends StyleCopyPasteBehaviors(fabric.Object)
  implements Connectable
{
  type = 'shape';
  noScaleCache = false;
  // So edges are sharp on large objects at high scales
  objectCaching = false;

  strokeUniform = true;
  transparentCorners = false;
  strokeLineCap = 'butt';
  strokeLineJoin = 'miter';
  strokeMiterLimit = 1000;
  minScaleLimit = 0.05;
  snapAngle = 45;
  snapThreshold = 5;
  borderOpacityWhenMoving = 0;

  // ShapeRecord or other linked shapes
  recordType?: WhiteboardRecordTypeNames;
  recordId?: RecordId;
  // This field is intentionally not serialized, as it will be fetched each time
  // the record is loaded anyway
  referenceNum?: string;
  cardData?: CardData;

  // External record info
  integrationRecord?: IntegrationRecord;

  mountedObjects: MountedObjects = {};

  textContent: string;
  wrapText?: boolean;

  // Type of the stroke/border. One of the LINE_TYPE_* constants from ToolStrokeStyle
  strokeType: StrokeType | null = null;
  strokeDouble: boolean = false;

  public shape: S;

  // From IDataStoreRecordBehaviors
  collaborate: boolean;

  colorFilterColor = 'transparent';
  colorFilterOpacity = 0.5;
  colorFilterMode: colorFilterMode = 'color';
  colorFilterPreviewColor: string | null = null; // Not serialized
  colorFilterPreviewMode: colorFilterMode | null = null; // Not serialized
  cropped?: boolean;
  croppedLeft?: number;
  croppedTop?: number;
  croppedWidth?: number;
  croppedHeight?: number;
  croppedScaleX?: number;
  croppedScaleY?: number;
  croppedHorizontalOffset?: number;
  croppedVerticalOffset?: number;
  originalLeft: number;
  originalTop: number;
  originalWidth: number;
  originalHeight: number;
  originalScaleX: number;
  originalScaleY: number;

  // Image masking
  maskType?: MaskType = 'none';

  // ShapeIcon specific keys.
  iconName?: string;

  // ShapeCustomPath
  shapePath?: string;

  // ShapeTimeline
  startDate?: number;
  endDate?: number;
  timescale?: string;
  hideWeekends?: boolean;
  fiscalMonth?: number;
  fiscalYearFormat?: string;

  // ShapeAnnotation
  tailOffsetX?: number;
  tailOffsetY?: number;
  tailRelativePosX?: number;
  tailRelativePosY?: number;
  isDraggingFromMenu: boolean = false;

  // Keys that will be serialized for saving and sending to remote systems.
  serializedKeys = [
    // Keys common to all shapes.
    'z',
    'shapeName',
    'variant',
    'textContent',
    'left',
    'top',
    'width',
    'height',
    'strokeType',
    'strokeDouble',
    'scaleX',
    'scaleY',
    'stroke',
    'strokeWidth',
    'opacity',
    'borderOpacity',
    'fill',
    'textOpacity',
    'textAlign',
    'textMargin',
    'verticalAlign',
    'fontSize',
    'angle',
    'container',
    'authorId',
    'showAuthor',
    'layoutPosition',
    'layoutManual',

    // ShapeText specific keys.
    'wrapText', // Whether text should wrap, or grow container

    // ShapeText or other label shapes specific keys for label case
    'connectorId', // label can only belong to one connector
    'labelPosition', // position along line, represented as percent between 0-1
    'labelOrientation', // position relative to line (e.g., above, below, etc.)

    // ShapeRecord specific keys.
    'recordType',
    'recordId',
    'cardData',

    // ShapeIntegrationRecord specific keys.
    'integrationRecord',

    // ShapeImage specific keys.
    'attachmentId',
    'colorFilterColor',
    'colorFilterOpacity',
    'colorFilterMode',
    'contentType',
    'cropped',
    'croppedLeft',
    'croppedTop',
    'croppedWidth',
    'croppedHeight',
    'croppedScaleX',
    'croppedScaleY',
    'croppedHorizontalOffset',
    'croppedVerticalOffset',
    'originalLeft',
    'originalTop',
    'originalWidth',
    'originalHeight',
    'originalScaleX',
    'originalScaleY',

    // Image Masking
    'maskType',

    // ShapeIcon and other icon shape specific keys.
    'iconName',

    // ShapeTimeline specific keys.
    'startDate', // Stored as unix timestamp
    'endDate', // Stored as unix timestamp
    'timescale',
    'hideWeekends',
    // These are currently not user configurable
    'fiscalMonth',
    'fiscalYearFormat',

    // ShapeAnnotation specific keys.
    'tailOffsetX',
    'tailOffsetY',
    'tailRelativePosX',
    'tailRelativePosY',

    // ShapeProgressBar specific keys.
    'progress',
    'textLocation', // Also used for milestones
    'variation', // Also used for milestones

    // ShapeCustomPath specific keys.
    'shapePath',
  ];

  shapeName: ShapeName;

  // maps to a variant definition in a definitions file - the shape uses that to look up
  // attributes for rendering, shape description etc. Only used for BusinessProcessBase shapes
  // for now, but could be used for other shape groups that render using consistent rules
  variant?: string;

  /** Handles the repeated work of creating a new shape and adding it to the canvas.
   * This method should be used instead of using new Shape directly for persisted shapes.
   *  */
  static create<S extends ShapeBase = ShapeBase>(options: ShapeOptions) {
    return options.controller.addToCanvas(new Shape<S>(options));
  }

  constructor(options: ShapeOptions) {
    // We have to call super before actually initializing with option values.
    super({});

    this.cornerStyle = 'circle';
    this.showAuthor = true;

    this.initializeConnectorAttachments();

    // Guarantee that shapeName is set before other properties. Some properties
    // may trigger a 'set' call on the shape, which will percolate to the shape
    // getter (via this.shape?.wasSet). If shapeName is not set when initializeShape
    // is called, we will throw an error when we try to convert from shapeName to class
    this.shapeName = options.shapeName;
    // We pre-initialize the options so they are available when the shape is
    // initialized. They will be initialized a second time in `setOptions`
    // to give code that watches `set` a chance to respond.
    Object.assign(this, options);
    super.setOptions({ ...options });
    this.initializeShape();

    this.initializeLockBehaviors(!!options.locked);

    // Create a text object if necessary
    this.addText(options.textContent ?? '');

    this.initializeSmartGuides();
    this.initializeFrameMember();

    this.on('mousedblclick', () => {
      if (
        ToolOpenRecord.available({
          controller: this.controller,
          selection: this,
        })
      ) {
        // This bypasses controller.actionApplyTool because when the whiteboard is read only,
        // selection from `controller.toolbarObject()` is null and the tool is not applied,
        // but we still want to open the drawer
        ToolOpenRecord.action({ controller: this.controller, selection: this });
      }
    });
  }

  initAfterBehaviorConstructors() {
    this.shape.initAfterShapeBehaviorConstructors();
  }

  get votingResultIcon(): JSX.Element {
    return (this.shape.constructor as typeof ShapeBase).renderIcon();
  }

  get votingResultSummary(): string {
    let summary = this.shape.description;
    if (!summary) {
      summary = hasEmbeddedTextBehaviors(this) ? this.plainText() : '';
    }
    return summary;
  }

  preventTextEditing(): boolean {
    return hasRecordReference(this) || hasIntegrationRecordReference(this);
  }

  allowedStyleCopyPasteFields(): string[] {
    return this.shape.allowedStyleCopyPasteFields();
  }

  get textProps(): TextProps {
    return this.shape.textProps;
  }

  get selectedTextFields() {
    return [];
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  deleted(controller) {
    this.connectorAttachments.forEach(attachment =>
      attachment.connector.detachObject(this)
    );
    // Clean up embeddedTextBehavior Intersection Observer instance
    this.removeObserver && this.removeObserver();
  }

  initializeShape(changingShape = false) {
    this.shapeName = sanitizeShapeName(this.shapeName);
    const ShapeClass = shapeNameToClass(this.shapeName);
    this.shape = new ShapeClass(this) as S;
    const constructor = this.shape.constructor as typeof ShapeClass;
    if (this.shape.hasFixedDimensions()) {
      this.set({
        width: this.width || constructor.defaultWidth(),
        height: this.height || constructor.defaultHeight(),
        strokeType: this.strokeType || constructor.defaultStrokeType(),
      });
    }

    if (changingShape) {
      void this.initializeText();
      this.shape.changedToShape();
      this.controller.actionHideObjectToolbar();
      this.controller.actionShowObjectToolbar();
    }
    this.controls = constructor.shapeControls;

    // Make sure we properly adjust label events when changing from/to text
    labelShapes.includes(this.shapeName)
      ? this.initializeLabelBehaviors()
      : this.clearLabelBehaviors();

    this.updateControlledProperties();

    // Update connector attachments to move them to the right place.
    this.synchronize();
  }

  tools(topLevel = true) {
    let shapeTools = this.shape.tools(topLevel);

    const hasRecord =
      hasRecordReference(this) || hasIntegrationRecordReference(this);

    if (hasRecord && !shapeTools.flat(10).includes(tools.ToolOpenRecord)) {
      shapeTools = [[tools.ToolOpenRecord], ...shapeTools];
    }

    if (!hasRecord) {
      shapeTools = shapeTools.concat(this.textTools());
    }

    return shapeTools;
  }

  // Currently only ShapeRecord uses this for resizing horizontally
  minDimension({ resizingWidth = true }) {
    return this.shape.minDimension({ resizingWidth });
  }

  maxDimension({ resizingWidth = true }) {
    return this.shape.maxDimension({ resizingWidth });
  }

  hasEmbeddedText() {
    return this.shape.hasEmbeddedText();
  }

  textClass() {
    return null;
  }

  calcTextMargin() {
    return this.shape.calcTextMargin();
  }

  setRecord(recordId: RecordId, recordType: WhiteboardRecordTypeNames) {
    this.set({ recordId, recordType });
  }

  // set any properties that are controlled by the shape subclass
  updateControlledProperties() {
    if (this.shape.getObjectProperties) {
      this.set(this.shape.getObjectProperties() as Partial<this>);
    }
  }

  getControlledProperties() {
    return this.shape.getObjectProperties?.();
  }

  _set(keyOrPath: KeyPath, value: unknown): fabric.Object {
    // Enforce minimum width for text shapes to avoid letting them get to 0 width,
    // which user can't recover from
    if (this.shapeName === 'text' && keyOrPath === 'width') {
      const minWidth = this.calculateMinShapeTextWidth();
      value = Math.max(minWidth, value as number);
    }

    super._set(keyOrPath, value);

    if (keyOrPath === 'shapeName' && this.canvas) {
      this.initializeShape(true);
      // Ensure that the "new" shape gets the shapeName wasSet call
      this.shape.wasSet(keyOrPath, value);
    }

    if (keyOrPath === 'textContent') {
      void this.setTextContent(value as string);
    }

    if (keyOrPath === 'strokeWidth' || keyOrPath === 'stroke') {
      this.synchronize();
    }

    if (keyOrPath === 'authorId' && value != null) {
      void this.controller.actionResolveUserInfo(value as RecordId);
    }

    // Let the shape know something changed.
    this.shape?.wasSet(keyOrPath, value);

    return this;
  }

  renderPath(ctx: CanvasRenderingContext2D) {
    if (this.shape.renderPath) {
      this.shape.renderPath(ctx);
    } else {
      super.renderPath(ctx);
    }
  }

  _renderIntegrationRecordLink(ctx: CanvasRenderingContext2D) {
    const object = this.shape.fabricObject;

    if (this.controller.canvas?.exporting || window.isPublishing) {
      return;
    }
    const recordData = object.integrationRecord?.recordData;
    if (!recordData) return;

    const maxHeight = 25;
    const scale = 1;

    const { scaleX = 1, scaleY = 1 } = object;

    const x = -object.width / 2;
    const y = -object.height / 2;
    const width = (object.width * scaleX) / scale;
    const height = (object.height * scaleY) / scale;
    const padding = 3;

    const normalizedPadding = normalizeSpacing(padding);

    const gap: SpacingAxisStruct = { horizontal: 3, vertical: 4 };

    const key = `link:${object.id}`;

    const buttonState: ButtonState =
      object.hoveredKey === key
        ? 'hovered'
        : object.isSelected()
        ? 'selected'
        : 'default';

    const { background, icon } = ButtonStyles[buttonState];

    ctx.save();
    ctx.translate(x, y);

    withContext({ canvas: object.canvas, ctx, object, scale }, () => {
      ctx.scale(1 / scaleX, 1 / scaleY);

      renderElement(
        <FlexRow
          gap={gap}
          width={width}
          height={height}
          padding={normalizedPadding}
          alignBottom
          maxHeight={Math.min(maxHeight, height * scaleY, 1)}
          maxRows={2}
          verticalAlignment='center'
        >
          <Rect
            key={key}
            padding={3}
            borderRadius={2}
            fill={formattedRgba(background)}
            onClick={() => {
              window.open(recordData.url, '_blank', 'noopener,noreferrer');
            }}
            tooltip={`Open ${recordData.recordId}`}
          >
            <SquareIcon
              icon={OPEN_RECORD}
              size={8}
              iconFill={formattedRgba(icon)}
            />
          </Rect>
        </FlexRow>
      );
    });

    ctx.restore();
  }

  _render(ctx: CanvasRenderingContext2D) {
    // Don't render objects that are too small to be visible.
    const zoom = this.canvas?.getZoom() ?? 1;
    if (
      this.width * this.scaleX * zoom < 0.1 ||
      this.height * this.scaleY * zoom < 0.1
    ) {
      return;
    }

    this.controller.renderManager.withRecordedHTMLCanvas(this, ctx, ctx => {
      // Reset mounted objects on render so that we don't have "ghost" interactive
      // areas
      this.mountedObjects = {};

      this.shape.render(ctx);

      // Reset the opacity before rendering the text.
      ctx.globalAlpha = 1;

      if (this.shape.shouldRenderText) {
        this.renderText(ctx);
      }

      if (
        hasIntegrationRecordReference(this) &&
        this.integrationRecord.recordData
      ) {
        this._renderIntegrationRecordLink(ctx);
      }
    });
  }

  attachmentPoints() {
    // For labels, remove attachment points
    if (labelShapes.includes(this.shapeName) && this.connectorId) {
      return [];
    }

    return this.shape.attachmentPoints();
  }

  connectControlDefinition() {
    // @ts-expect-error TS doesn't like us using the constructor to access static methods like this
    return this.shape.constructor.connectControlDefinition();
  }

  containsPoint(pointer, lines, absolute, calculate) {
    let superContains = super.containsPoint(
      pointer,
      lines,
      absolute,
      calculate
    );
    if (superContains) {
      return true;
    }

    // See if the pointer is in the text area.
    if (this.textLocation) {
      // Translate the absolute mouse position to the viewport.
      const adjustedPointer = this.canvas.restorePointerVpt(pointer);

      superContains = pointInRect(adjustedPointer, this.textPosition());
      if (superContains) {
        return true;
      }
    }

    // See if shape has any additional containsPoint logic.
    if (this.shape.containsPoint) {
      return this.shape.containsPoint(
        pointer,
        lines,
        absolute,
        calculate,
        superContains
      );
    } else {
      return superContains;
    }
  }

  isOnScreen(calculate) {
    if (!this.canvas) return false;

    let superIsOnScreen = super.isOnScreen(calculate);
    if (superIsOnScreen) {
      return true;
    }

    if (this.textLocation) {
      const canvas = this.canvas;
      if (!canvas.vptCoords) {
        return false;
      }
      const pointTL = canvas.vptCoords.tl;
      const pointBR = canvas.vptCoords.br;
      const textRect = this.textPosition();

      // Check if textRect is on screen.
      superIsOnScreen =
        pointTL.x < textRect.left + textRect.width &&
        pointBR.x > textRect.left &&
        pointTL.y < textRect.top + textRect.height &&
        pointBR.y > textRect.top;
      if (superIsOnScreen) {
        return true;
      }
    }

    if (this.shape.isOnScreen) {
      return this.shape.isOnScreen(calculate, superIsOnScreen);
    } else {
      return superIsOnScreen;
    }
  }

  hasExternalContent() {
    if (this.shape.hasExternalContent) {
      return this.shape.hasExternalContent();
    } else if (this.canvas?.isHTMLExport() && !this.textContentIsEmpty()) {
      // Special case for shapes that contain text when exporting to HTML
      return true;
    } else {
      return false;
    }
  }

  clipExternalContent(): boolean {
    if (this.shape.clipExternalContent) {
      return this.shape.clipExternalContent();
    } else {
      return true;
    }
  }

  renderExternalContent() {
    return this.shape.renderExternalContent();
  }

  renderClipBoundary(ctx) {
    return this.shape.renderClipBoundary(ctx);
  }

  private calculateMinShapeTextWidth(): number {
    // Guard for non text shapes. No other shape (should) have adjustable width
    if (this.shapeName !== 'text') {
      return (this.shape.constructor as typeof ShapeBase).defaultWidth();
    }

    const { fontSize = 14 } = this;
    const margin = this.calcTextMargin();
    const horizontalMargins = margin.left + margin.right;

    // Font size is correlated to scale, so adjust for scale when calculating
    // "true" min width. This should be enough for 1 character at given font size
    return fontSize / this.scaleX + horizontalMargins / 2;
  }

  toClipboardHTML() {
    if (this.textContent) {
      return `<p>${this.textContent}</p>`;
    }
    return null;
  }
}

export default Shape;
